<template>
  <div class="musubi-page musubi-page--signals">
    <header class="musubi-page__head">
      <h1>Buyer intents</h1>
      <p class="text-muted">
        Score discovered leads on what they want to buy (intent signals, grouped into buyer intents)
        and on how well they fit your ideal client profile (fit criteria) — independently of each other.
      </p>
    </header>

    <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>
    <p v-if="message" class="alert alert-success py-2 small">{{ message }}</p>

    <!-- SECTION 1: Intent signals, grouped by buyer intent -->
    <section class="card mb-4">
      <div class="card-body">
        <div class="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-2">
          <div>
            <h2 class="h5 mb-1">Intent signals</h2>
            <p class="text-muted small mb-1">
              A buyer intent groups the signals that indicate a prospect wants to buy — e.g. "Website
              rebuild", "Switching agency". Signals score leads; grouping organises them by what the
              prospect actually wants.
            </p>
            <p class="text-muted small mb-0">
              Each match adds its weight; a <strong>warm</strong> signal flips the lead to direct-route
              (overrides score). Match types: keyword/phrase (case-insensitive substring) or regex.
            </p>
          </div>
          <div class="d-flex gap-2 flex-shrink-0">
            <button class="btn btn-outline-primary btn-sm" @click="openCreate">
              <CygnusIcon name="plus" class="me-1" />New signal
            </button>
            <button class="btn btn-link btn-sm" :disabled="loading" @click="load">
              <CygnusIcon name="refresh" /> Refresh
            </button>
          </div>
        </div>

        <section v-if="showCreate" class="editor card mb-3">
          <div class="card-body">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <h3 class="h6 mb-0">{{ editingId ? `Edit signal #${editingId}` : 'New signal' }}</h3>
              <button type="button" class="btn btn-link btn-sm" @click="cancelEdit">Cancel</button>
            </div>
            <form @submit.prevent="save">
              <div class="row g-2 mb-2">
                <div class="col-md-5">
                  <label class="form-label small text-muted">Name</label>
                  <input v-model="form.name" class="form-control form-control-sm" placeholder="Asks for help" required>
                </div>
                <div class="col-md-3">
                  <label class="form-label small text-muted">Match type</label>
                  <select v-model="form.match_type" class="form-select form-select-sm">
                    <option value="keyword">keyword</option>
                    <option value="phrase">phrase</option>
                    <option value="regex">regex</option>
                  </select>
                </div>
                <div class="col-md-2">
                  <label class="form-label small text-muted">Weight</label>
                  <input v-model.number="form.weight" type="number" class="form-control form-control-sm">
                </div>
                <div class="col-md-2 d-flex align-items-end gap-3">
                  <label class="small"><input type="checkbox" v-model="form.warm_override"> Warm</label>
                  <label class="small"><input type="checkbox" v-model="form.active"> Active</label>
                </div>
              </div>
              <div class="row g-2 mb-2">
                <div class="col-md-6">
                  <label class="form-label small text-muted">Buyer intent (group)</label>
                  <input
                    v-model="form.intent_group"
                    class="form-control form-control-sm"
                    list="intent-group-options"
                    placeholder="e.g. Website rebuild — blank = Ungrouped"
                  >
                </div>
              </div>
              <div class="mb-2">
                <label class="form-label small text-muted">Pattern</label>
                <input v-model="form.pattern" class="form-control form-control-sm" placeholder="looking for help" required>
              </div>
              <button type="submit" class="btn btn-primary btn-sm" :disabled="busy || !form.name || !form.pattern">
                {{ editingId ? 'Save changes' : 'Create signal' }}
              </button>
            </form>
          </div>
        </section>

        <div v-if="loading" class="text-muted small py-3">Loading…</div>
        <div v-else-if="!rows.length" class="text-muted small py-3">No signals yet.</div>
        <div v-else>
          <div v-for="group in groupedSignals" :key="group.name" class="signal-group mb-3">
            <div class="signal-group__head d-flex align-items-center gap-2 mb-1">
              <template v-if="renamingGroup === group.name">
                <input
                  v-model="renameValue"
                  class="form-control form-control-sm"
                  style="max-width: 240px"
                  placeholder="New intent name"
                  @keyup.enter="commitRename(group)"
                  @keyup.esc="cancelRename"
                >
                <button class="btn btn-sm btn-primary" :disabled="busy" @click="commitRename(group)">Save</button>
                <button class="btn btn-sm btn-link" @click="cancelRename">Cancel</button>
              </template>
              <template v-else>
                <h3 class="h6 mb-0">
                  {{ group.name }} <span class="text-muted small">({{ group.signals.length }})</span>
                </h3>
                <button
                  v-if="group.name !== 'Ungrouped'"
                  class="btn btn-sm btn-link p-0"
                  :disabled="busy"
                  @click="startRename(group)"
                >Rename intent</button>
              </template>
            </div>
            <div class="table-responsive">
              <table class="table table-sm signal-table mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th style="width: 100px">Type</th>
                    <th>Pattern</th>
                    <th style="width: 90px">Weight</th>
                    <th style="width: 70px" class="text-center">Warm</th>
                    <th style="width: 70px" class="text-center">Active</th>
                    <th style="width: 140px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="s in group.signals" :key="s.id">
                    <td>{{ s.name }}</td>
                    <td>{{ s.match_type }}</td>
                    <td class="signal-table__pattern">{{ s.pattern }}</td>
                    <td>{{ s.weight }}</td>
                    <td class="text-center">{{ s.warm_override ? '🔥' : '—' }}</td>
                    <td class="text-center">{{ s.active ? '✓' : '—' }}</td>
                    <td>
                      <button class="btn btn-sm btn-link p-0 me-3" @click="startEdit(s.id)">Edit</button>
                      <button class="btn btn-sm btn-link p-0 text-danger" @click="remove(s.id)">Delete</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <datalist id="intent-group-options">
          <option v-for="g in intentGroupNames" :key="g" :value="g" />
        </datalist>
      </div>
    </section>

    <!-- SECTION 2: Fit criteria -->
    <section class="card mb-4">
      <div class="card-body">
        <div class="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-2">
          <div>
            <h2 class="h5 mb-1">Fit criteria</h2>
            <p class="text-muted small mb-0">
              Fit criteria score how well a discovered company matches your ideal client — by framework,
              tech, agency, sector — independent of buyer intent.
            </p>
          </div>
          <div class="d-flex align-items-center gap-2 flex-shrink-0">
            <button class="btn btn-outline-primary btn-sm" @click="openFitCreate">
              <CygnusIcon name="plus" class="me-1" />New criterion
            </button>
            <button class="btn btn-link btn-sm" :disabled="fitLoading" @click="loadFit">
              <CygnusIcon name="refresh" /> Refresh
            </button>
            <button class="btn btn-outline-secondary btn-sm" :disabled="rescoring" @click="rescore">
              {{ rescoring ? 'Rescoring…' : 'Rescore cold leads' }}
            </button>
            <span v-if="rescoreResult !== null" class="small text-muted">Rescored {{ rescoreResult }}.</span>
          </div>
        </div>

        <section v-if="showFitCreate" class="editor card mb-3">
          <div class="card-body">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <h3 class="h6 mb-0">{{ fitEditingId ? `Edit criterion #${fitEditingId}` : 'New criterion' }}</h3>
              <button type="button" class="btn btn-link btn-sm" @click="cancelFitEdit">Cancel</button>
            </div>
            <form @submit.prevent="saveFit">
              <div class="row g-2 mb-2">
                <div class="col-md-4">
                  <label class="form-label small text-muted">Name</label>
                  <input v-model="fitForm.name" class="form-control form-control-sm" placeholder="Uses React" required>
                </div>
                <div class="col-md-2">
                  <label class="form-label small text-muted">Category</label>
                  <input v-model="fitForm.category" class="form-control form-control-sm" placeholder="Tech stack">
                </div>
                <div class="col-md-2">
                  <label class="form-label small text-muted">Field</label>
                  <select v-model="fitForm.field" class="form-select form-select-sm">
                    <option value="all">all</option>
                    <option value="company">company</option>
                    <option value="headline">headline</option>
                    <option value="text">text</option>
                    <option value="framework">framework</option>
                    <option value="tech">tech</option>
                    <option value="agency">agency</option>
                  </select>
                </div>
                <div class="col-md-2">
                  <label class="form-label small text-muted">Match type</label>
                  <select v-model="fitForm.match_type" class="form-select form-select-sm">
                    <option value="keyword">keyword</option>
                    <option value="phrase">phrase</option>
                    <option value="regex">regex</option>
                  </select>
                </div>
                <div class="col-md-1">
                  <label class="form-label small text-muted">Weight</label>
                  <input v-model.number="fitForm.weight" type="number" class="form-control form-control-sm">
                </div>
                <div class="col-md-1 d-flex align-items-end">
                  <label class="small"><input type="checkbox" v-model="fitForm.active"> Active</label>
                </div>
              </div>
              <div class="mb-2">
                <label class="form-label small text-muted">Pattern</label>
                <input v-model="fitForm.pattern" class="form-control form-control-sm" placeholder="react" required>
              </div>
              <button type="submit" class="btn btn-primary btn-sm" :disabled="fitBusy || !fitForm.name || !fitForm.pattern">
                {{ fitEditingId ? 'Save changes' : 'Create criterion' }}
              </button>
            </form>
          </div>
        </section>

        <MusubiTabulator
          :rows="fitRows"
          :columns="fitColumns"
          storage-key="admin.fit-criteria"
          :initial-sort="{ column: 'weight', dir: 'desc' }"
          :page-size="25"
          :loading="fitLoading"
          :error="error"
        />
      </div>
    </section>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";
import MusubiTabulator from "@root/components/MusubiTabulator.vue";
import { esc } from "./util/cellHtml";

export default {
  name: 'AdminSignals',
  components: { MusubiTabulator },
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      rows: [],
      fitRows: [],
      loading: false,
      fitLoading: false,
      busy: false,
      fitBusy: false,
      rescoring: false,
      rescoreResult: null,
      error: null,
      message: '',
      showCreate: false,
      editingId: null,
      form: { name: '', match_type: 'keyword', pattern: '', weight: 10, warm_override: false, active: true, intent_group: '' },
      showFitCreate: false,
      fitEditingId: null,
      fitForm: { name: '', match_type: 'keyword', field: 'all', category: '', pattern: '', weight: 10, active: true },
      renamingGroup: null,
      renameValue: '',
    };
  },
  computed: {
    groupedSignals() {
      const map = new Map();
      for (const s of this.rows) {
        const key = (s.intent_group && String(s.intent_group).trim()) || 'Ungrouped';
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(s);
      }
      const groups = [...map.entries()].map(([name, signals]) => ({ name, signals }));
      groups.sort((a, b) => {
        if (a.name === 'Ungrouped') return 1;
        if (b.name === 'Ungrouped') return -1;
        return a.name.localeCompare(b.name);
      });
      return groups;
    },
    intentGroupNames() {
      const set = new Set();
      for (const s of this.rows) {
        const v = s.intent_group && String(s.intent_group).trim();
        if (v) set.add(v);
      }
      return [...set].sort((a, b) => a.localeCompare(b));
    },
    fitColumns() {
      return [
        { key: 'id',          label: 'ID',       width: 60,  sorter: 'number' },
        { key: 'name',        label: 'Name' },
        { key: 'category',    label: 'Category', width: 130 },
        { key: 'field',       label: 'Field',     width: 100 },
        { key: 'match_type',  label: 'Type',      width: 100 },
        { key: 'pattern',     label: 'Pattern' },
        { key: 'weight',      label: 'Weight',    width: 90,  sorter: 'number' },
        { key: 'active',      label: 'Active',    width: 80,  hozAlign: 'center', format: (v) => v ? '✓' : '—' },
        { key: 'actions',     label: 'Actions',   width: 150, sorter: false, headerFilter: false,
          format: (v, row) => `<button class="btn btn-sm btn-link p-0 me-3 fit-act" data-id="${esc(row.id)}" data-action="edit">Edit</button>`
            + `<button class="btn btn-sm btn-link p-0 text-danger fit-act" data-id="${esc(row.id)}" data-action="delete">Delete</button>` },
      ];
    },
  },
  mounted() {
    this.load();
    this.loadFit();
    document.addEventListener('click', this.onDelegatedClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDelegatedClick);
  },
  methods: {
    // ---- intent signals ----
    async load() {
      this.loading = true; this.error = null;
      try {
        const res = await this.api.get('/api/v1/admin/leads/signals');
        const payload = res?.data?.data ?? res?.data ?? res;
        this.rows = Array.isArray(payload) ? payload : (payload?.rows || []);
      } catch (e) { this.error = e?.message || 'Failed to load signals.'; }
      this.loading = false;
    },
    blankForm() {
      return { name: '', match_type: 'keyword', pattern: '', weight: 10, warm_override: false, active: true, intent_group: '' };
    },
    openCreate() {
      if (this.showCreate && !this.editingId) { this.showCreate = false; return; }
      this.editingId = null;
      this.form = this.blankForm();
      this.message = '';
      this.showCreate = true;
    },
    cancelEdit() {
      this.showCreate = false;
      this.editingId = null;
      this.form = this.blankForm();
    },
    startEdit(id) {
      const s = this.rows.find((r) => Number(r.id) === Number(id));
      if (!s) return;
      this.editingId = Number(id);
      this.form = {
        name: s.name || '',
        match_type: s.match_type || 'keyword',
        pattern: s.pattern || '',
        weight: Number(s.weight) || 0,
        warm_override: !!s.warm_override,
        active: s.active === undefined ? true : !!s.active,
        intent_group: s.intent_group || '',
      };
      this.message = '';
      this.showCreate = true;
    },
    async save() {
      this.busy = true; this.error = null; this.message = '';
      const body = {
        name: this.form.name,
        match_type: this.form.match_type,
        pattern: this.form.pattern,
        weight: this.form.weight,
        warm_override: this.form.warm_override ? 1 : 0,
        active: this.form.active ? 1 : 0,
        intent_group: (this.form.intent_group || '').trim(),
      };
      try {
        if (this.editingId) {
          await this.api.put(`/api/v1/admin/leads/signals/${this.editingId}`, body);
          this.message = `Signal #${this.editingId} updated.`;
        } else {
          await this.api.post('/api/v1/admin/leads/signals', body);
          this.message = 'Signal created.';
        }
        this.cancelEdit();
        await this.load();
      } catch (e) { this.error = e?.response?.data?.error || e?.message || 'Save failed.'; }
      this.busy = false;
    },
    async remove(id) {
      if (!window.confirm(`Delete signal #${id}? Leads it already scored keep their score until re-crawled.`)) return;
      this.error = null; this.message = '';
      try {
        await this.api.delete(`/api/v1/admin/leads/signals/${id}`);
        this.message = `Signal #${id} deleted.`;
        if (this.editingId === Number(id)) this.cancelEdit();
        await this.load();
      } catch (e) { this.error = e?.response?.data?.error || e?.message || 'Delete failed.'; }
    },
    // ---- buyer-intent group rename ----
    startRename(group) {
      this.renamingGroup = group.name;
      this.renameValue = group.name === 'Ungrouped' ? '' : group.name;
      this.error = null; this.message = '';
    },
    cancelRename() {
      this.renamingGroup = null;
      this.renameValue = '';
    },
    async commitRename(group) {
      const newName = (this.renameValue || '').trim();
      if (!newName) { this.error = 'Intent name cannot be empty.'; return; }
      if (newName === group.name) { this.cancelRename(); return; }
      this.busy = true; this.error = null; this.message = '';
      try {
        for (const s of group.signals) {
          await this.api.put(`/api/v1/admin/leads/signals/${s.id}`, { intent_group: newName });
        }
        this.message = `Renamed "${group.name}" to "${newName}".`;
        this.renamingGroup = null; this.renameValue = '';
        await this.load();
      } catch (e) { this.error = e?.response?.data?.error || e?.message || 'Rename failed.'; }
      this.busy = false;
    },
    // ---- fit criteria ----
    async loadFit() {
      this.fitLoading = true; this.error = null;
      try {
        const res = await this.api.get('/api/v1/admin/leads/fit-criteria');
        const payload = res?.data?.data ?? res?.data ?? res;
        this.fitRows = Array.isArray(payload) ? payload : (payload?.rows || []);
      } catch (e) { this.error = e?.message || 'Failed to load fit criteria.'; }
      this.fitLoading = false;
    },
    fitBlankForm() {
      return { name: '', match_type: 'keyword', field: 'all', category: '', pattern: '', weight: 10, active: true };
    },
    openFitCreate() {
      if (this.showFitCreate && !this.fitEditingId) { this.showFitCreate = false; return; }
      this.fitEditingId = null;
      this.fitForm = this.fitBlankForm();
      this.message = '';
      this.showFitCreate = true;
    },
    cancelFitEdit() {
      this.showFitCreate = false;
      this.fitEditingId = null;
      this.fitForm = this.fitBlankForm();
    },
    startFitEdit(id) {
      const c = this.fitRows.find((r) => Number(r.id) === Number(id));
      if (!c) return;
      this.fitEditingId = Number(id);
      this.fitForm = {
        name: c.name || '',
        match_type: c.match_type || 'keyword',
        field: c.field || 'all',
        category: c.category || '',
        pattern: c.pattern || '',
        weight: Number(c.weight) || 0,
        active: c.active === undefined ? true : !!c.active,
      };
      this.message = '';
      this.showFitCreate = true;
    },
    async saveFit() {
      this.fitBusy = true; this.error = null; this.message = '';
      const body = {
        name: this.fitForm.name,
        pattern: this.fitForm.pattern,
        match_type: this.fitForm.match_type,
        field: this.fitForm.field,
        category: this.fitForm.category,
        weight: this.fitForm.weight,
        active: this.fitForm.active ? 1 : 0,
      };
      try {
        if (this.fitEditingId) {
          await this.api.put(`/api/v1/admin/leads/fit-criteria/${this.fitEditingId}`, body);
          this.message = `Criterion #${this.fitEditingId} updated.`;
        } else {
          await this.api.post('/api/v1/admin/leads/fit-criteria', body);
          this.message = 'Fit criterion created.';
        }
        this.cancelFitEdit();
        await this.loadFit();
      } catch (e) { this.error = e?.response?.data?.error || e?.message || 'Save failed.'; }
      this.fitBusy = false;
    },
    async removeFit(id) {
      if (!window.confirm(`Delete fit criterion #${id}?`)) return;
      this.error = null; this.message = '';
      try {
        await this.api.delete(`/api/v1/admin/leads/fit-criteria/${id}`);
        this.message = `Criterion #${id} deleted.`;
        if (this.fitEditingId === Number(id)) this.cancelFitEdit();
        await this.loadFit();
      } catch (e) { this.error = e?.response?.data?.error || e?.message || 'Delete failed.'; }
    },
    async rescore() {
      this.rescoring = true; this.error = null; this.message = '';
      try {
        const res = await this.api.post('/api/v1/admin/leads/fit/rescore', {});
        const payload = res?.data ?? res;
        const n = Number(payload?.rescored ?? 0);
        this.rescoreResult = n;
        this.message = `Rescored ${n} cold lead${n === 1 ? '' : 's'}.`;
      } catch (e) { this.error = e?.response?.data?.error || e?.message || 'Rescore failed.'; }
      this.rescoring = false;
    },
    onDelegatedClick(ev) {
      const btn = ev.target.closest('.fit-act');
      if (!btn) return;
      ev.preventDefault(); ev.stopPropagation();
      const id = Number(btn.dataset.id);
      if (!id) return;
      if (btn.dataset.action === 'delete') this.removeFit(id);
      else if (btn.dataset.action === 'edit') this.startFitEdit(id);
    },
  },
};
</script>

<style>
.musubi-page--signals { padding: var(--m-space-6, 32px); }
.actions-bar { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
.editor { background: var(--m-bg-card, #FFF); }
.signal-group { border: 1px solid var(--m-border, #E2E8F0); border-radius: var(--m-radius, 6px); padding: 12px; background: var(--m-bg-card, #FFF); }
.signal-group__head h3 { color: var(--m-text-primary, #0F172A); }
.signal-table { font-size: 0.85rem; margin-bottom: 0; }
.signal-table th { color: var(--m-text-secondary, #64748B); text-transform: uppercase; font-size: 0.7rem; letter-spacing: 0.04em; font-weight: 600; border-top: none; }
.signal-table__pattern { max-width: 320px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
</style>
